import { useState } from 'react'
import { Button, Empty, Flex, Input, Popover, Spin, Typography } from 'antd'
import { CloseOutlined, PlusOutlined } from '@ant-design/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClockRotateLeft } from '@fortawesome/pro-solid-svg-icons'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useDebouncedState } from '../../hooks/useDebouncedState'
import { useChatHistorySearch, buildChatHistoryParams } from './chatHistoryQueries'
import ChatHistoryListItem from './ChatHistoryListItem'

const { Text } = Typography

type Props = {
  onNewChat: () => void
  onSelectChat: (sessionId: string) => void
}

const ChatHistoryDropdown = ({ onNewChat, onSelectChat }: Props) => {
  const [open, setOpen] = useState(false)
  const { state: search, debouncedState: debouncedSearch, setState: setSearch } = useDebouncedState('', 300)

  const params = open ? buildChatHistoryParams(debouncedSearch) : null
  const { data, fetchNextPage, hasNextPage, isLoading } = useChatHistorySearch(params)

  const allResults = data?.pages.flatMap((page) => page.results) ?? []

  const handleNewChat = () => {
    onNewChat()
    setOpen(false)
  }

  const handleSelectChat = (sessionId: string) => {
    onSelectChat(sessionId)
    setOpen(false)
  }

  const content = (
    <div style={{ width: 320 }}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 8 }}>
        <Text strong style={{ fontSize: 14 }}>Chat History</Text>
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          onClick={() => setOpen(false)}
        />
      </Flex>

      <Button
        type="default"
        icon={<PlusOutlined />}
        onClick={handleNewChat}
        style={{ width: '100%', marginBottom: 8 }}
      >
        New Chat
      </Button>

      <Input
        placeholder="Search chats..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        allowClear
        className="chat-history-search"
      />

      <div
        id="chat-history-scrollable"
        style={{ maxHeight: 400, overflowY: 'auto', marginTop: 8 }}
      >
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : allResults.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No previous chats"
          />
        ) : (
          <InfiniteScroll
            dataLength={allResults.length}
            next={fetchNextPage}
            hasMore={!!hasNextPage}
            loader={<div style={{ textAlign: 'center', padding: 8 }}><Spin size="small" /></div>}
            scrollableTarget="chat-history-scrollable"
          >
            {allResults.map((chat) => (
              <ChatHistoryListItem
                key={chat.session_id}
                sessionId={chat.session_id}
                title={chat.title}
                startedAt={chat.started_at}
                onClick={handleSelectChat}
              />
            ))}
          </InfiniteScroll>
        )}
      </div>
    </div>
  )

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      arrow={false}
    >
      <Button size="small" style={{ borderRadius: 16, padding: '0 14px', gap: 4 }}>
        <FontAwesomeIcon icon={faClockRotateLeft} />
        Chat History
      </Button>
    </Popover>
  )
}

export default ChatHistoryDropdown
